/**
 * Closing the free-inference card, and keeping it closed.
 *
 * The same shape as the announcement banner's dismissal, with one deliberate
 * difference: `localStorage` rather than `sessionStorage`. A maintenance notice
 * dismissed on Monday should come back on Tuesday, because the platform is
 * still going to be down — a verdict on an application is read once and
 * finished with, and bringing it back in a new tab would be nagging somebody
 * about news they have already had.
 */
const DISMISSED_KEY = "maxlayer.free-inference.dismissed";

/**
 * Keyed on the organization *and* the verdict.
 *
 * The organization, because somebody who belongs to several should not have one
 * of them silenced by closing another's card.
 *
 * The verdict, for the reason the announcement banner keys on a revision: a
 * dismissal should hide what was read, not what happens next. Closing "under
 * review" and then being approved is new information, and a card keyed on the
 * campaign alone would have swallowed it — leaving somebody with a key in their
 * inbox and a dashboard that never mentioned it.
 *
 * The two terminal verdicts never change again, so dismissing one of those is
 * permanent in practice, which is what closing a finished message should mean.
 */
export const dismissalKey = (orgId: string, status: string) => `${orgId}:${status}`;

/**
 * Whether the card still belongs on the screen.
 *
 * Pure and exported so the rule that is easy to get backwards can be tested
 * without rendering anything: a decision that arrives after a dismissal comes
 * back.
 */
export function isDismissed(
  dismissed: ReadonlySet<string>,
  orgId: string | undefined,
  status: string | undefined,
): boolean {
  if (!orgId || !status) return false;
  return dismissed.has(dismissalKey(orgId, status));
}

/**
 * Reads defensively, because this is the one input here the product does not
 * control. Private browsing throws on access, a hand-edited value parses to
 * anything, and neither is a reason to fail rendering a dashboard.
 */
export function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [],
    );
  } catch {
    return new Set();
  }
}

/** Remembers one dismissal, and says what the set became. */
export function rememberDismissal(key: string): Set<string> {
  const next = readDismissed();
  next.add(key);
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...next]));
  } catch {
    /* Storage full, or blocked. The card closes for this page either way, which
       is what the click asked for; it simply comes back next time. */
  }
  return next;
}
